import { t } from 'i18next';
import { ValueOf } from 'ts-essentials';
import { v4 as uuidv4 } from 'uuid';

import { isApiError } from 'Libs/http/utils';

import { PartialContent, PartialContentForCampaignForm } from '../activity/activity.type';
import { CONTENT_STATUS } from './common/constants';
import type { ContentFilters } from './common/types';

export const CONTENT_STATUS_ID_MAP = Object.values(CONTENT_STATUS).reduce((map, value, index) => {
  map[value] = index;
  return map;
}, {} as Record<ValueOf<typeof CONTENT_STATUS>, number>);

export const CONTENT_ID_STATUS_MAP = Object.values(CONTENT_STATUS).reduce((map, value, index) => {
  map[index] = value;
  return map;
}, {} as Record<number, ValueOf<typeof CONTENT_STATUS>>);

export const getIndicatorText = (status: typeof CONTENT_STATUS[keyof typeof CONTENT_STATUS]): string => {
  switch (status) {
    case CONTENT_STATUS.WAITING_TRANSLATION:
      return t('contents:status.pending_translation');
    case CONTENT_STATUS.WAITING_VALIDATION:
      return t('contents:status.require_validation');
    case CONTENT_STATUS.DRAFT:
      return t('contents:status.draft');
    default:
      return '';
  }
};

const preEncodeArray = (arrayFilter: ReadonlyArray<string>): string => {
  return arrayFilter.toString();
};

export type ContentQueryFilters = {
  status?: Readonly<ValueOf<typeof CONTENT_STATUS>>;
  archived?: Readonly<'true' | 'false'>;
  languageCode?: Readonly<string>;
  thematicId?: Readonly<string>;
  name?: Readonly<string>;
  campaignId?: Readonly<string>;
  contributorId?: Readonly<string>;
};

const buildStatus = (selected: ContentFilters['status']) => {
  if (selected === null) {
    return;
  }
  return selected;
};

const buildArchived = (selected: ContentFilters['archived']) => {
  if (selected === null) {
    return 'false';
  }
  return selected ? 'true' : 'false';
};

const buildLanguageCodes = (selected: ContentFilters['languageCodes']) => {
  if (selected.length === 0) {
    return;
  }
  return preEncodeArray(selected);
};

const buildThematic = (selected: ContentFilters['thematicId']) => {
  if (selected === null) {
    return;
  }
  return selected.toString();
};

const buildName = (selected: ContentFilters['queries']) => {
  if (selected.length === 0) {
    return;
  }
  return preEncodeArray(selected);
};

const buildContributor = (selected: ContentFilters['userIds']) => {
  if (selected.length === 0) {
    return;
  }
  return selected.toString();
};

const buildCampaign = (selected: ContentFilters['activityIds']) => {
  if (selected.length === 0) {
    return;
  }
  return selected.toString();
};

export const buildQueryFilterFromFilters = (filters: ContentFilters): ContentQueryFilters => {
  const queryFilter: ContentQueryFilters = {
    status: buildStatus(filters.status),
    archived: buildArchived(filters.archived),
    languageCode: buildLanguageCodes(filters.languageCodes),
    thematicId: buildThematic(filters.thematicId),
    contributorId: buildContributor(filters.userIds),
    campaignId: buildCampaign(filters.activityIds),
    name: buildName(filters.queries),
  };

  const queryFilterKeys = Object.keys(queryFilter) as (keyof typeof queryFilter)[];
  queryFilterKeys.forEach((key) => queryFilter[key] === undefined && delete queryFilter[key]);

  return queryFilter;
};

export const extractContentApiError = (error: any) => {
  if (!isApiError(error)) {
    return 'unknown_error';
  }
  if (error?.params) {
    return error.params;
  }
  return error.message;
};

export const convertPartialContentForCampaignForm = (content: PartialContent): PartialContentForCampaignForm => ({
  ...content,
  id: uuidv4(),
  contentId: content.id,
});

export const convertPartialContentFromCampaignForm = (content: PartialContentForCampaignForm): PartialContent => ({
  ...content,
  id: content.contentId,
});
